import React, { useState, useCallback, useMemo } from 'react';
import { View, StyleSheet, ScrollView } from 'react-native';
import { getYear, getMonth, startOfWeek, endOfWeek } from 'date-fns';
import Button from 'design-system/Button';
import { rem } from 'design-system/values';
import { TextButton } from 'design-system/TextButton';
import useStyles from 'hooks/useStyles';
import Modal from 'utils/packages/Modal';
import createStyleSheets from 'utils/createStyleSheets';
import { useMarginBottom } from 'hooks/useMarginBottom';
import useIsCompactCalendar from 'design-system/calendar/useIsCompactCalendar';
import { useScrollableModalMaxHeight } from 'hooks';
import useColors from 'hooks/useColors';
import { ButtonGroup } from 'design-system/ButtonGroup';
import Header from './Header';
import Controller from './Controller';
import { isWeb } from '../../constants';
import { AnalyticsFilterContext } from './context';
import { filterStartYear, defaultSelectedSegmentIndex } from './constants';
const currentYear = getYear(new Date());
export const getYearArrArr = (start, end, selected) => {
    const yearData = [];
    let hasStart = false;
    let endRef = end;
    const periodLength = isWeb ? 12 : 6;
    while (!hasStart) {
        const startRef = endRef - periodLength + 1;
        const arr = Array.from({ length: endRef - startRef + 1 }, (_, i) => startRef + i);
        yearData.unshift(arr);
        endRef -= periodLength;
        if (startRef < start) {
            hasStart = true;
        }
    }
    const defaultYearIndex = yearData.findIndex((data) => data.includes(selected));
    return {
        yearData,
        defaultYearIndex: defaultYearIndex > -1 ? defaultYearIndex : 0,
    };
};
const MIN_HEIGHT = rem(720);
const HEADER_HEIGHT = rem(100);
const AnalyticsFilter = ({ onSave, onReset, showReset, defaults, }) => {
    const { yearData, defaultYearIndex } = getYearArrArr(filterStartYear, currentYear, defaults?.yearly || currentYear);
    /**
     * Segment control state
     */
    const [selectedTabIndex, setSelectedTabIndex] = useState(() => {
        if (defaults?.weeklyRange) {
            return 0;
        }
        if (defaults?.monthly !== undefined) {
            return 1;
        }
        if (defaults?.quarterly !== undefined) {
            return 2;
        }
        if (defaults?.yearly !== undefined) {
            return 3;
        }
        if (defaults?.custom) {
            return 4;
        }
        return defaultSelectedSegmentIndex;
    });
    /**
     * Weekly segment states start
     */
    const [weeklyRange, setWeeklyRange] = useState(defaults?.weeklyRange);
    /**
     * Weekly segment states end
     */
    /**
     * Monthly segment states start
     */
    // Stores year as number
    // Stores month as index
    const [selectedYear, setSelectedYear] = useState(defaults?.monthly?.currentYear ||
        defaults?.quarterly?.currentYear ||
        currentYear);
    const [selectedMonth, setSelectedMonth] = useState(defaults?.monthly?.currentMonth);
    /**
     * Monthly segment states end
     */
    const [selectedQuarter, setSelectedQuarter] = useState(defaults?.quarterly?.currentQuarter);
    /**
     * Year segment states start
     */
    const [selectedYearFilter, setSelectedYearFilter] = useState(defaults?.yearly);
    const [selectedYearFilterIndex, setSelectedYearFilterIndex] = useState(defaultYearIndex);
    /**
     * Year segment states end
     */
    /**
     * Custom segment states start
     */
    const [to, setTo] = useState(defaults?.custom?.[1]);
    const [from, setFrom] = useState(defaults?.custom?.[0]);
    /**
     * Custom segment states end
     */
    const currentStates = useMemo(() => ({
        currentYear: getYear(new Date()),
        currentMonth: getMonth(new Date()),
        todayDateStr: new Date().toISOString().slice(0, 10),
    }), 
    // recalculate the above variables on filter open/close
    []);
    const handleSetSelectedTabIndex = useCallback((index) => {
        setSelectedTabIndex(index);
        switch (index) {
            case 0: {
                const now = new Date();
                setWeeklyRange([
                    startOfWeek(now, { weekStartsOn: 1 }),
                    endOfWeek(now, { weekStartsOn: 1 }),
                ]);
                break;
            }
            case 1:
                setSelectedYear(currentStates.currentYear);
                setSelectedMonth(currentStates.currentMonth);
                break;
            case 2:
                setSelectedYear(currentStates.currentYear);
                setSelectedQuarter(Math.floor(currentStates.currentMonth / 3) + 1);
                break;
            case 3: {
                setSelectedYearFilter(currentStates.currentYear);
                const idx = yearData.findIndex((d) => d.includes(currentStates.currentYear));
                if (idx > -1)
                    setSelectedYearFilterIndex(idx);
                break;
            }
            default:
                break;
        }
    }, [currentStates, yearData]);
    const handleSave = useCallback(() => {
        switch (selectedTabIndex) {
            case 0:
                if (weeklyRange) {
                    onSave({
                        from: weeklyRange[0],
                        to: weeklyRange[1],
                        type: 'week',
                    });
                }
                break;
            case 1:
                if (selectedMonth !== undefined) {
                    onSave({
                        month: selectedMonth + 1,
                        year: selectedYear,
                        type: 'month',
                    });
                }
                break;
            case 2:
                if (selectedQuarter !== undefined) {
                    onSave({
                        year: selectedYear,
                        quarter: selectedQuarter,
                        type: 'quarter',
                    });
                }
                break;
            case 3:
                if (selectedYearFilter !== undefined) {
                    onSave({ year: selectedYearFilter, type: 'year' });
                }
                break;
            case 4:
                if (from && to) {
                    onSave({ from, to, type: 'custom' });
                }
                break;
            default:
                break;
        }
    }, [
        from,
        onSave,
        selectedMonth,
        selectedQuarter,
        selectedTabIndex,
        selectedYear,
        selectedYearFilter,
        to,
        weeklyRange,
    ]);
    const styles = useStyles(modalStyleSet);
    const paddingBottom = useMarginBottom('paddingBottom', undefined, styles.container);
    const compact = useIsCompactCalendar();
    const contextValue = useMemo(() => ({
        selectedTabIndex,
        setSelectedTabIndex: handleSetSelectedTabIndex,
        ...currentStates,
        selectedYear,
        selectedMonth,
        setSelectedYear,
        setSelectedMonth,
        selectedQuarter,
        setSelectedQuarter,
        weeklyRange,
        setWeeklyRange,
        yearData,
        defaultYearIndex,
        selectedYearFilter,
        setSelectedYearFilter,
        selectedYearFilterIndex,
        setSelectedYearFilterIndex,
        to,
        from,
        setTo,
        setFrom,
    }), [
        currentStates,
        defaultYearIndex,
        from,
        handleSetSelectedTabIndex,
        selectedMonth,
        selectedQuarter,
        selectedTabIndex,
        selectedYear,
        selectedYearFilter,
        selectedYearFilterIndex,
        to,
        weeklyRange,
        yearData,
    ]);
    const maxHeight = useScrollableModalMaxHeight(-HEADER_HEIGHT);
    const colors = useColors();
    const style = useMemo(() => ({ maxHeight, backgroundColor: colors.background.dark }), [colors.background.dark, maxHeight]);
    const isScrollable = !(isWeb || maxHeight >= MIN_HEIGHT);
    const buttonGroup = useMemo(() => (<ButtonGroup type={isScrollable ? 'floating' : 'default'} isInIOS13Modal margins={!isScrollable} style={isScrollable
            ? [
                paddingBottom,
                {
                    paddingHorizontal: rem(16),
                    borderTopColor: colors.borders.divider,
                    backgroundColor: colors.cards.onDark,
                    borderTopWidth: rem(1),
                    bottom: 0,
                    left: 0,
                    right: 0,
                },
            ]
            : undefined}>
        {showReset ? (<TextButton style={[styles.resetButton, compact && styles.resetButtonCompact]} onPress={onReset} text="Reset period"/>) : null}

        <Button brand onPress={handleSave} title="Save" disabled={selectedTabIndex === 4 && (!from || !to)}/>
      </ButtonGroup>), [
        colors.borders.divider,
        colors.cards.onDark,
        compact,
        from,
        handleSave,
        isScrollable,
        onReset,
        paddingBottom,
        selectedTabIndex,
        showReset,
        styles.resetButton,
        styles.resetButtonCompact,
        to,
    ]);
    const content = useMemo(() => (<View style={[paddingBottom, !isWeb && { minHeight: MIN_HEIGHT }]}>
        <AnalyticsFilterContext.Provider value={contextValue}>
          <Header />
          <Controller />
          {!isScrollable && buttonGroup}
        </AnalyticsFilterContext.Provider>
      </View>), [buttonGroup, contextValue, isScrollable, paddingBottom]);
    if (!isScrollable) {
        return content;
    }
    return (<>
      <ScrollView style={style}>{content}</ScrollView>
      {buttonGroup}
    </>);
};
const useAnalyticsFilter = () => {
    const show = useCallback((onSave, onReset, defaults) => {
        Modal.show(<AnalyticsFilter onSave={onSave} onReset={onReset} showReset={onReset !== undefined} defaults={defaults}/>, {
            lightBg: true,
            containerStyle: styles.modal,
            title: isWeb ? 'Filter period' : undefined,
        });
    }, []);
    const hide = useCallback(() => {
        Modal.hide();
    }, []);
    return [show, hide];
};
export default useAnalyticsFilter;
const styles = StyleSheet.create({
    modal: {
        margin: 0,
        paddingBottom: 0,
        paddingHorizontal: 0,
        padding: 0,
    },
});
const modalStyleSet = createStyleSheets((colors) => ({
    container: {
        ...(!isWeb && {
            backgroundColor: colors.background.dark,
        }),
    },
    resetButton: {
        alignSelf: 'center',
        marginTop: rem(8),
        marginBottom: rem(16),
    },
    resetButtonCompact: {
        marginBottom: rem(8),
    },
    buttonContainer: {
        alignItems: 'center',
        marginHorizontal: rem(16),
    },
}));
